import React, { useState } from 'react';
import {
  UserCircleIcon, ShieldExclamationIcon, LockClosedIcon,
  PencilSquareIcon, CheckCircleIcon, XCircleIcon, EyeIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, usePagination, useDebounce, usePermission } from '../../hooks';
import { usersAPI, authAPI } from '../../services/api';
import {
  SearchInput, Button, Badge, StatusBadge, Modal, Input, Select,
  ConfirmDialog, Pagination, LoadingPage, EmptyState, Avatar, Card,
  SectionHeader, Tabs,
} from '../../components/common/UI';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const ROLE_OPTS = ['customer','business_owner','business_staff','moderator','admin','super_admin'];
const ROLE_BADGE = { super_admin:'red', admin:'purple', moderator:'blue', business_owner:'yellow', business_staff:'yellow', customer:'gray' };

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: '1', label: 'Active' },
  { value: '0', label: 'Suspended' },
];

// ── Edit user modal ───────────────────────────────────
const EditUserModal = ({ user, open, onClose, onSaved }) => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { first_name: user?.first_name, last_name: user?.last_name, role: user?.role },
  });
  const { mutate, loading } = useMutation(
    (d) => usersAPI.update(user.id, d),
    { successMsg: 'User updated', onSuccess: onSaved }
  );
  return (
    <Modal open={open} onClose={onClose} title="Edit User"
      footer={<div className="flex justify-end gap-2"><Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button><Button variant="primary" size="sm" onClick={handleSubmit(mutate)} loading={loading}>Save</Button></div>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input label="First name" error={errors.first_name?.message} {...register('first_name', { required: true })} />
          <Input label="Last name" {...register('last_name')} />
        </div>
        <Select label="Role" {...register('role')}>
          {ROLE_OPTS.map(r => <option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
        </Select>
      </div>
    </Modal>
  );
};

// ── User detail drawer ────────────────────────────────
const UserDetail = ({ userId, open, onClose }) => {
  const { data, loading } = useQuery(() => usersAPI.get(userId), [userId], { enabled: !!userId && open });
  const { mutate: blockTokens, loading: blocking } = useMutation(
    (d) => authAPI.blockTokens(d),
    { successMsg: 'All tokens revoked' }
  );

  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title="User Details" size="lg">
      {loading ? <LoadingPage /> : data && (
        <div className="space-y-5">
          <div className="flex items-start gap-4">
            <Avatar name={`${data.first_name} ${data.last_name}`} size="xl" src={data.avatar_url} />
            <div className="flex-1">
              <h3 className="font-display font-semibold text-surface-900 text-lg">{data.first_name} {data.last_name}</h3>
              <p className="text-sm text-surface-500">{data.email}</p>
              <p className="text-sm text-surface-500">{data.phone}</p>
              <div className="mt-2 flex items-center gap-2">
                <Badge variant={ROLE_BADGE[data.role] || 'gray'}>{data.role?.replace(/_/g,' ')}</Badge>
                <StatusBadge status={data.is_active ? 'active' : 'suspended'} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 rounded-xl bg-surface-50 p-4 text-sm">
            <div><span className="text-surface-400">Email verified</span><p className="font-medium mt-0.5">{data.is_email_verified ? '✓ Yes' : '✗ No'}</p></div>
            <div><span className="text-surface-400">Phone verified</span><p className="font-medium mt-0.5">{data.is_phone_verified ? '✓ Yes' : '✗ No'}</p></div>
            <div><span className="text-surface-400">Joined</span><p className="font-medium mt-0.5">{data.created_at ? format(new Date(data.created_at), 'dd MMM yyyy') : '—'}</p></div>
            <div><span className="text-surface-400">Last login</span><p className="font-medium mt-0.5">{data.last_login_at ? format(new Date(data.last_login_at), 'dd MMM yyyy') : '—'}</p></div>
          </div>
          <div className="flex gap-2">
            <Button variant="danger" size="sm" icon={LockClosedIcon} loading={blocking}
              onClick={() => blockTokens({ userId: data.id, reason: 'admin_revoke' })}>
              Revoke All Tokens
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ── Main page ─────────────────────────────────────────
export default function UsersPage() {
  const { isSuperAdmin } = usePermission();
  const { page, setPage, reset } = usePagination();
  const [search, setSearch] = useState('');
  const [role,   setRole]   = useState('');
  const [status, setStatus] = useState('');
  const [editUser,  setEditUser]  = useState(null);
  const [viewUser,  setViewUser]  = useState(null);
  const [confirmSuspend, setConfirmSuspend] = useState(null);

  const dSearch = useDebounce(search);

  const { data, loading, refetch } = useQuery(
    () => usersAPI.list({
      page,
      limit: 20,
      search: dSearch || undefined,
      role: role || undefined,
      status: status || undefined,
    }),
    [page, dSearch, role, status]
  );

  const { mutate: suspend, loading: suspending } = useMutation(
    (d) => usersAPI.suspend(d),
    { successMsg: 'User suspended', onSuccess: () => { refetch(); setConfirmSuspend(null); } }
  );
  const { mutate: unsuspend } = useMutation(
    (id) => usersAPI.unsuspend(id),
    { successMsg: 'User reactivated', onSuccess: refetch }
  );

  const users = Array.isArray(data)
    ? data
    : (data?.rows || data?.data?.rows || (Array.isArray(data?.data) ? data.data : Array.isArray(data?.users) ? data.users : []));
  const total = data?.count ?? data?.total ?? data?.pagination?.total ?? data?.data?.count ?? (Array.isArray(users) ? users.length : 0);
  const pages = (data?.pagination?.pages ?? Math.ceil(total / 20)) || 1;

  return (
    <div className="animate-in space-y-5">
      <SectionHeader title="Customers & Users" subtitle={`${total} total users`} />

      <Card>
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <SearchInput value={search} onChange={(v) => { setSearch(v); reset(); }} placeholder="Search by name, email, phone…" className="flex-1 min-w-[200px]" />
          <select className="input w-44" value={role} onChange={e => { setRole(e.target.value); reset(); }}>
            <option value="">All Roles</option>
            {ROLE_OPTS.map(r => <option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
          </select>
          <div className="w-52">
            <Tabs tabs={STATUS_TABS} active={status} onChange={(v) => { setStatus(v); reset(); }} />
          </div>
        </div>

        {loading ? <LoadingPage /> : users.length === 0 ? (
          <EmptyState icon={UserCircleIcon} title="No users found" description="Try adjusting your filters" />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th><th>Role</th><th>Phone</th><th>Status</th><th>Joined</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <Avatar name={`${u.first_name} ${u.last_name}`} src={u.avatar_url} size="sm" />
                        <div>
                          <p className="font-medium text-surface-900">{u.first_name} {u.last_name}</p>
                          <p className="text-xs text-surface-400">{u.email || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td><Badge variant={ROLE_BADGE[u.role] || 'gray'}>{u.role?.replace(/_/g,' ')}</Badge></td>
                    <td><span className="font-mono text-xs">{u.phone || '—'}</span></td>
                    <td><StatusBadge status={u.is_active ? 'active' : 'suspended'} /></td>
                    <td className="text-surface-400 text-xs">{u.created_at ? format(new Date(u.created_at), 'dd MMM yy') : '—'}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button className="btn btn-ghost p-1.5 rounded-lg" onClick={() => setViewUser(u.id)} title="View"><EyeIcon className="h-4 w-4" /></button>
                        <button className="btn btn-ghost p-1.5 rounded-lg" onClick={() => setEditUser(u)} title="Edit"><PencilSquareIcon className="h-4 w-4" /></button>
                        {u.is_active
                          ? <button className="btn btn-ghost p-1.5 rounded-lg text-danger-500 hover:text-danger-700" onClick={() => setConfirmSuspend(u)} title="Suspend"><XCircleIcon className="h-4 w-4" /></button>
                          : <button className="btn btn-ghost p-1.5 rounded-lg text-success-600" onClick={() => unsuspend(u.id)} title="Activate"><CheckCircleIcon className="h-4 w-4" /></button>
                        }
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>

      {editUser && <EditUserModal user={editUser} open={!!editUser} onClose={() => setEditUser(null)} onSaved={() => { refetch(); setEditUser(null); }} />}
      <UserDetail userId={viewUser} open={!!viewUser} onClose={() => setViewUser(null)} />
      <ConfirmDialog
        open={!!confirmSuspend} danger
        title="Suspend User"
        message={`Are you sure you want to suspend ${confirmSuspend?.first_name} ${confirmSuspend?.last_name}? All their tokens will be revoked.`}
        confirmText="Suspend"
        loading={suspending}
        onClose={() => setConfirmSuspend(null)}
        onConfirm={() => suspend({ userId: confirmSuspend?.id, reason: 'account_suspended' })}
      />
    </div>
  );
}
